import { createApi } from "@reduxjs/toolkit/query/react";

import ENDPOINTS from "../../lib/api/endpoints";
import { axiosBaseQuery } from "../../lib/api/rtkBaseQuery";
import {
  mapCounts,
  normalizePage,
  toMasterOrder,
  toOrderDetail,
  toOrderListItem,
} from "./ordersMappers";

export const ordersApi = createApi({
  reducerPath: "ordersApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Order"],
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getOrders: builder.query({
      query: ({ statusGroup, size }) => ({
        url: ENDPOINTS.ORDERS.root,
        params: {
          page: 0,
          size,
          sort: "createdAt,desc",
          statusGroup,
        },
      }),
      transformResponse: (dto) => {
        const page = normalizePage(dto);

        return { ...page, items: page.items.map(toOrderListItem).filter(Boolean) };
      },
      providesTags: (result, error, { statusGroup }) => [
        { type: "Order", id: statusGroup || "LIST" },
        { type: "Order", id: "LIST" },
      ],
    }),

    getOrderCounts: builder.query({
      query: () => ({ url: ENDPOINTS.ORDERS.counts }),
      transformResponse: mapCounts,
      providesTags: [{ type: "Order", id: "COUNTS" }],
    }),

    getOrderDetail: builder.query({
      query: (orderId) => ({ url: ENDPOINTS.ORDERS.byId(orderId) }),
      transformResponse: toOrderDetail,
      providesTags: (result, error, orderId) => [{ type: "Order", id: orderId }],
    }),

    getMasterOrder: builder.query({
      query: (masterId) => ({ url: ENDPOINTS.ORDERS.masterById(masterId) }),
      transformResponse: toMasterOrder,
      providesTags: (result, error, masterId) => [
        { type: "Order", id: `master-${masterId}` },
      ],
    }),

    cancelOrder: builder.mutation({
      query: ({ orderId, reason }) => ({
        url: ENDPOINTS.ORDERS.cancel(orderId),
        method: "POST",
        data: reason ? { reason } : {},
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "Order", id: orderId },
        { type: "Order", id: "PENDING" },
        { type: "Order", id: "PROCESSING" },
        { type: "Order", id: "PAST" },
        { type: "Order", id: "LIST" },
        { type: "Order", id: "COUNTS" },
      ],
    }),

    reorder: builder.mutation({
      query: ({ orderId }) => ({
        url: ENDPOINTS.ORDERS.reorder(orderId),
        method: "POST",
        data: {},
      }),
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderDetailQuery,
  useGetMasterOrderQuery,
  useGetOrderCountsQuery,
  useCancelOrderMutation,
  useReorderMutation,
} = ordersApi;
